import { Form, Formik, type FormikProps } from "formik";
import { isEqual } from "lodash-es";
import type React from "react";
import { useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import AssetUseCaseSelect from "../../../components/Asset/AssetUseCaseSelect.tsx";
import AssetPositionGroupSelectCombo from "../../../components/AssetPositionGroup/AssetPositionGroupSelectCombo.tsx";
import { ButtonGroupField } from "../../../components/Forms/ButtonGroup";
import SchemeSelectFormComponent from "../../../components/Scheme/SchemeSelectFormComponent.tsx";
import { Button } from "../../../components/Theme/button.tsx";
import { Col, Container } from "../../../components/Theme/grid.tsx";
import {
  convertStringUndefined,
  createSearchString,
} from "../../../components/navigation/SearchString.tsx";
import {
  type GetIssuesIssueGetQueryParams,
  issueSeverityEnum,
} from "../../../kubb";
import AssetTypeSelect from "../../assetRegister/assets/AssetTypeSelect.tsx";

type IssueListFilterFormProps = {
  filters: GetIssuesIssueGetQueryParams;
  includeSchemeFilter?: boolean;
  includeCancelledFilter?: boolean;
  onSubmit?: (values: GetIssuesIssueGetQueryParams) => void;
};

export default function IssueListFilterForm(
  props: IssueListFilterFormProps,
): React.ReactElement {
  const { includeSchemeFilter = false, includeCancelledFilter = true } = props;
  const navigate = useNavigate();
  const formikRef = useRef<FormikProps<GetIssuesIssueGetQueryParams>>(null);

  const setFiltersToRoute = useCallback(
    (values: any): void => {
      if (!props.includeSchemeFilter) {
        values.scheme_id = undefined;
      }

      const newSearchString = createSearchString(
        convertStringUndefined({ ...values }),
      );

      if (
        location.search === ""
          ? newSearchString !== ""
          : `?${newSearchString}` !== location.search
      ) {
        navigate({
          ...location,
          search: newSearchString,
        });
      }
    },
    [navigate, props.includeSchemeFilter],
  );

  return (
    <>
      <Formik
        innerRef={formikRef}
        initialValues={props.filters}
        enableReinitialize={true}
        onSubmit={(x): void => {
          if (props.onSubmit) {
            props.onSubmit(x);
          } else {
            setFiltersToRoute(x);
          }
        }}
      >
        {({ values }): React.ReactElement => (
          <Form>
            <Container gap={3}>
              {includeSchemeFilter && (
                <Col sm={12}>
                  <SchemeSelectFormComponent
                    allowAll={true}
                    fieldName={"scheme_id"}
                    label={{ label: "Scheme", size: "sm" }}
                  />
                </Col>
              )}
              <Col sm={12} md={6}>
                <AssetTypeSelect
                  fieldName={"asset_type"}
                  label={{ label: "Asset Type", size: "sm" }}
                  allowUnknown={true}
                  isMulti={true}
                />
              </Col>
              <Col sm={12} md={6}>
                <AssetUseCaseSelect
                  fieldName={"use_case"}
                  label={{ label: "Use Case", size: "sm" }}
                  includeAll={false}
                  isMulti={true}
                  includeNone={true}
                />
              </Col>
              <Col sm={12} md={3}>
                <AssetPositionGroupSelectCombo
                  fieldName={"group"}
                  label={{ label: "Group", size: "sm" }}
                  useGivenSchemeId={includeSchemeFilter}
                  schemeId={includeSchemeFilter ? values.scheme_id : undefined}
                />
              </Col>
              {/*<Col sm={12} md={2}>*/}
              {/*  <ButtonGroupField*/}
              {/*    fieldName={"status"}*/}
              {/*    label={{ label: "Asset Position Status", size: "sm" }}*/}
              {/*    options={[*/}
              {/*      { label: "Active", value: "ACTIVE" },*/}
              {/*      {*/}
              {/*        label: "Deleted",*/}
              {/*        value: "DELETED",*/}
              {/*      },*/}
              {/*    ]}*/}
              {/*    fullWidth={true}*/}
              {/*  />*/}
              {/*</Col>*/}
              <Col sm={12} md={3}>
                <ButtonGroupField
                  fieldName={"severity"}
                  label={{ label: "Issue Severity", size: "sm" }}
                  options={[
                    {
                      label: "All",
                      value: [
                        issueSeverityEnum.ERROR,
                        issueSeverityEnum.WARNING,
                      ],
                    },
                    { label: "Error", value: [issueSeverityEnum.ERROR] },
                    {
                      label: "Warning",
                      value: [issueSeverityEnum.WARNING],
                    },
                  ]}
                  fullWidth={true}
                />
              </Col>
              <Col sm={12} md={2}>
                <ButtonGroupField
                  fieldName={"include_closed"}
                  label={{ label: "Include Closed?", size: "sm" }}
                  options={[
                    {
                      label: "Yes",
                      value: true,
                    },
                    { label: "No", value: false },
                  ]}
                  fullWidth={true}
                />
              </Col>
              {includeCancelledFilter && (
                <Col sm={12} md={2}>
                  <ButtonGroupField
                    fieldName={"include_cancelled"}
                    label={{ label: "Include Cancelled?", size: "sm" }}
                    options={[
                      {
                        label: "Yes",
                        value: true,
                      },
                      { label: "No", value: false },
                    ]}
                    fullWidth={true}
                  />
                </Col>
              )}
              <Col sm={12} md={2} className={"mt-auto"} end={true}>
                <Button
                  type={"submit"}
                  className={"w-full md:h-[38px]"}
                  disabled={isEqual(values, props.filters)}
                >
                  Filter
                </Button>
              </Col>
            </Container>
          </Form>
        )}
      </Formik>
    </>
  );
}
